import { ParticipationStatus, StudyType, TabStatus } from '@app/api';
import { getColorForParticipationStatus } from 'lib/participationUtils';
import { ChipColor } from '@app/core';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Tab } from '@mui/material';

interface Tab {
  label: string;
  statusList: {
    color: ChipColor;
    statuses: ParticipationStatus[];
    label?: string;
  }[];
  adminOnly?: boolean;
  visibleOnDataOnly?: boolean;
}

type TabConfig = { [key in TabStatus]?: Tab };

const useParticipationDetailTabs = (): Record<TabStatus, Tab> => {
  const { t } = useTranslation();
  return useMemo(
    () => ({
      outOfQuota: {
        label: t('sessions.details.participationsDetails.tabs.outOfQuota'),
        statusList: [
          {
            color: getColorForParticipationStatus(
              ParticipationStatus.OUT_OF_QUOTA,
            ),
            statuses: [ParticipationStatus.OUT_OF_QUOTA],
          },
        ],
      },
      applied: {
        label: t('sessions.details.participationsDetails.tabs.applied'),
        statusList: [
          {
            label: t('sessions.details.participationsDetails.tabs.applied'),
            color: getColorForParticipationStatus(ParticipationStatus.APPLIED),
            statuses: [ParticipationStatus.APPLIED],
          },
        ],
        visibleOnDataOnly: true,
      },
      selected: {
        label: t('sessions.details.participationsDetails.tabs.selected'),
        statusList: [
          {
            label: t(
              'sessions.details.monitoring.columnTitle.accepted_moderated',
            ),
            color: getColorForParticipationStatus(ParticipationStatus.ACCEPTED),
            statuses: [ParticipationStatus.ACCEPTED],
          },
          {
            label: t('sessions.details.monitoring.columnTitle.booked'),
            color: getColorForParticipationStatus(ParticipationStatus.BOOKED),
            statuses: [ParticipationStatus.BOOKED],
          },
          {
            label: t(
              'sessions.details.monitoring.columnTitle.participated_moderated',
            ),
            color: getColorForParticipationStatus(
              ParticipationStatus.PARTICIPATED,
            ),
            statuses: [ParticipationStatus.PARTICIPATED],
          },
          {
            label: t(
              'sessions.details.monitoring.columnTitle.payment_requested',
            ),
            color: getColorForParticipationStatus(ParticipationStatus.PAYED),
            statuses: [
              ParticipationStatus.PAYMENT_REQUESTED,
              ParticipationStatus.PROCESSING_PAYMENT,
              ParticipationStatus.PAYED,
            ],
          },
          {
            label: t(
              'sessions.details.monitoring.contactModal.filters.canceled',
            ),
            color: getColorForParticipationStatus(ParticipationStatus.CANCELED),
            statuses: [
              ParticipationStatus.CANCELED,
              ParticipationStatus.NO_SHOW,
            ],
          },
        ],
      },
      approved: {
        label: t('sessions.details.participationsDetails.tabs.approved'),
        statusList: [
          {
            label: t('sessions.details.monitoring.columnTitle.accepted'),
            color: getColorForParticipationStatus(ParticipationStatus.ACCEPTED),
            statuses: [ParticipationStatus.ACCEPTED],
          },
          {
            label: t('sessions.details.monitoring.columnTitle.in_progress'),
            color: getColorForParticipationStatus(
              ParticipationStatus.IN_PROGRESS,
            ),
            statuses: [ParticipationStatus.IN_PROGRESS],
          },
          {
            label: t(
              'sessions.details.monitoring.contactModal.filters.noshow_unmoderated',
            ),
            color: getColorForParticipationStatus(ParticipationStatus.NO_SHOW),
            statuses: [ParticipationStatus.NO_SHOW],
          },
        ],
      },
      participated: {
        label: t('sessions.details.participationsDetails.tabs.participated'),
        statusList: [
          {
            color: getColorForParticipationStatus(
              ParticipationStatus.PARTICIPATED,
            ),
            statuses: [
              ParticipationStatus.PARTICIPATED,
              ParticipationStatus.PAYMENT_REQUESTED,
              ParticipationStatus.PROCESSING_PAYMENT,
              ParticipationStatus.PAYED,
            ],
          },
        ],
      },
      gaveUp: {
        label: t('sessions.details.participationsDetails.tabs.gaveUp'),
        statusList: [
          {
            color: getColorForParticipationStatus(ParticipationStatus.CANCELED),
            statuses: [ParticipationStatus.CANCELED],
          },
        ],
      },
      rejected: {
        label: t('sessions.details.participationsDetails.tabs.rejected'),
        statusList: [
          {
            color: getColorForParticipationStatus(ParticipationStatus.DECLINED),
            statuses: [ParticipationStatus.DECLINED],
          },
        ],
      },
      other_moderated: {
        label: t('sessions.details.participationsDetails.tabs.other'),
        adminOnly: true,
        visibleOnDataOnly: true,
        statusList: [
          {
            color: 'purple',
            statuses: [ParticipationStatus.NOT_INTERESTED],
          },
        ],
      },
      other_unmoderated: {
        label: t('sessions.details.participationsDetails.tabs.other'),
        adminOnly: true,
        visibleOnDataOnly: true,
        statusList: [
          {
            color: 'purple',
            statuses: [
              ParticipationStatus.APPLIED,
              ParticipationStatus.NOT_INTERESTED,
            ],
          },
        ],
      },
    }),
    [t],
  );
};

const excludedByStudyType: Record<StudyType, TabStatus[]> = {
  [StudyType.UNMODERATED]: [
    TabStatus.applied,
    TabStatus.selected,
    TabStatus.other_moderated,
  ],
  [StudyType.MODERATED]: [
    TabStatus.approved,
    TabStatus.participated,
    TabStatus.gaveUp,
    TabStatus.other_unmoderated,
  ],
  [StudyType.DIARY]: [],
};

export const useStudyTypeDetailTabs = (
  studyType: StudyType,
  canSeeQuota: boolean,
) => {
  const excludedList = excludedByStudyType[studyType];
  const participationDetailTabs = useParticipationDetailTabs();

  return useMemo(() => {
    const final: TabConfig = {};
    for (const key of Object.keys(participationDetailTabs) as TabStatus[]) {
      if (excludedList.includes(key)) {
        continue;
      }

      if (!canSeeQuota && key === TabStatus.outOfQuota) {
        continue;
      }

      final[key] = participationDetailTabs[key];
    }

    return final;
  }, [participationDetailTabs, excludedList, canSeeQuota]);
};

export const getFirstVisibleTab = (
  tabs: TabConfig,
  isAdmin: boolean = false,
  countByStatus?: Record<ParticipationStatus, number>,
): TabStatus => {
  for (const [key, tabConfig] of Object.entries(tabs) as Array<
    [TabStatus, Tab]
  >) {
    if (tabConfig.adminOnly && !isAdmin) {
      continue;
    }

    if (tabConfig.visibleOnDataOnly && countByStatus) {
      const totalCount = tabConfig.statusList.reduce((acc, statusGroup) => {
        const groupCount = statusGroup.statuses.reduce(
          (sum, status) => sum + (countByStatus[status] ?? 0),
          0,
        );
        return acc + groupCount;
      }, 0);

      if (totalCount === 0) {
        continue;
      }
    }

    return key;
  }

  return Object.keys(tabs)[0] as TabStatus;
};
